import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Tag, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WelcomePopup = () => {
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    // Don't show on admin pages
    if (window.location.pathname.startsWith('/admin')) return;
    const seen = localStorage.getItem('welcome_popup_seen');
    if (seen) return;

    axios.get(`${API}/welcome-popup`).then(res => {
      if (res.data.enabled) {
        setConfig(res.data);
        setTimeout(() => setVisible(true), 2000);
      }
    }).catch(() => {});
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!config?.show_timer || !visible) return;
    const end = Date.now() + (config.timer_hours || 24) * 3600000;
    const tick = () => {
      const diff = Math.max(0, end - Date.now());
      setCountdown({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [config, visible]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(config.discount_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [config]);

  const handleClose = useCallback(() => {
    setVisible(false);
    localStorage.setItem('welcome_popup_seen', Date.now().toString());
  }, []);

  const handleShop = useCallback(() => {
    handleClose();
    navigate('/shop');
  }, [handleClose, navigate]);

  if (!visible || !config) return null;

  const message = (config.message || '')
    .replace('{code}', config.discount_code)
    .replace('{discount}', config.discount_percent);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="welcome-popup-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-[#1A2B3C] rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
        data-testid="welcome-popup"
      >
        {/* Gold top strip */}
        <div className="h-1.5 bg-gradient-to-r from-[#D4AF37] via-[#E8C860] to-[#D4AF37]" />

        {/* Close */}
        <button
          onClick={handleClose}
          data-testid="welcome-popup-close"
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-10"
        >
          <X size={20} />
        </button>

        <div className="p-8 text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            <Tag size={28} className="text-[#D4AF37]" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-2">{config.title}</h2>
          <p className="text-white/70 text-sm leading-relaxed mb-6">{message}</p>

          {/* Discount code */}
          {config.discount_code && (
            <div className="mb-6">
              <button
                onClick={handleCopy}
                data-testid="copy-discount-code"
                className="inline-flex items-center gap-2 bg-white/10 border-2 border-dashed border-[#D4AF37]/60 rounded-lg px-6 py-3 hover:bg-white/15 transition-colors group"
              >
                <span className="text-[#D4AF37] font-bold text-xl tracking-widest">{config.discount_code}</span>
                {copied ? (
                  <Check size={18} className="text-emerald-400" />
                ) : (
                  <Copy size={18} className="text-white/40 group-hover:text-white/70 transition-colors" />
                )}
              </button>
              <p className="text-white/40 text-xs mt-2">
                {copied ? 'Copied to clipboard!' : 'Tap to copy code'}
              </p>
            </div>
          )}

          {/* Timer */}
          {config.show_timer && (
            <div className="flex items-center justify-center gap-1 mb-6" data-testid="welcome-popup-timer">
              <Clock size={14} className="text-[#D4AF37]" />
              <span className="text-white/60 text-xs">Offer expires in</span>
              <span className="text-[#D4AF37] font-mono font-bold text-sm">
                {String(countdown.h).padStart(2, '0')}:{String(countdown.m).padStart(2, '0')}:{String(countdown.s).padStart(2, '0')}
              </span>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleShop}
            data-testid="welcome-popup-cta"
            className="w-full bg-[#D4AF37] hover:bg-[#E8C860] text-[#1A2B3C] font-bold py-3.5 rounded-lg text-sm uppercase tracking-wider transition-colors"
          >
            {config.button_text || 'Shop Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
